import React, { useState, useEffect } from 'react'
import { GoogleDriveFolder } from '../types'
import {
  ConnectionCard,
  ConnectionTitle,
  ActionButton,
  ButtonGroup,
  FormGroup,
  Label,
  InputField,
  Checkbox,
  CheckboxLabel,
  ErrorMessage
} from '../style'
import { FolderPicker } from './FolderPicker'

interface AddSyncConfigModalProps {
  isOpen: boolean
  folders: GoogleDriveFolder[]
  isLoadingFolders: boolean
  onClose: () => void
  onFetchFolders: (parentId?: string) => Promise<GoogleDriveFolder[]>
  onSave: (
    googleFolderId: string,
    googleFolderName: string,
    targetDataRoomPath: string,
    syncSubfolders: boolean
  ) => Promise<void>
}

export const AddSyncConfigModal: React.FC<AddSyncConfigModalProps> = ({
  isOpen,
  folders,
  isLoadingFolders,
  onClose,
  onFetchFolders,
  onSave
}) => {
  const [selectedFolder, setSelectedFolder] = useState<GoogleDriveFolder | null>(null)
  const [targetPath, setTargetPath] = useState('/Google Drive')
  const [syncSubfolders, setSyncSubfolders] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      onFetchFolders()
      setSelectedFolder(null)
      setTargetPath('/Google Drive')
      setSyncSubfolders(false)
      setError(null)
    }
  }, [isOpen, onFetchFolders])

  const handleSave = async () => {
    if (!selectedFolder) {
      setError('Please select a folder')
      return
    }
    if (!targetPath.trim()) {
      setError('Please enter a target path')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      await onSave(selectedFolder.id, selectedFolder.name, targetPath, syncSubfolders)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration')
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <ConnectionCard style={{ maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
        <ConnectionTitle>Add Folder to Sync</ConnectionTitle>

        {error && <ErrorMessage style={{ marginBottom: '16px' }}>{error}</ErrorMessage>}

        <FormGroup style={{ marginBottom: '16px' }}>
          <Label>Select Google Drive Folder</Label>
          <FolderPicker
            folders={folders}
            selectedFolderId={selectedFolder?.id}
            isLoading={isLoadingFolders}
            onSelect={setSelectedFolder}
            onNavigate={(folderId) => onFetchFolders(folderId)}
          />
        </FormGroup>

        {selectedFolder && (
          <div style={{ marginBottom: '16px', color: '#4CAF50' }}>
            Selected: {selectedFolder.name}
          </div>
        )}

        <FormGroup style={{ marginBottom: '16px' }}>
          <Label>Target Data Room Path</Label>
          <InputField
            value={targetPath}
            onChange={(e) => setTargetPath(e.target.value)}
            placeholder="/Google Drive/Folder Name"
          />
        </FormGroup>

        <CheckboxLabel style={{ marginBottom: '24px' }}>
          <Checkbox
            checked={syncSubfolders}
            onChange={(e) => setSyncSubfolders(e.target.checked)}
          />
          Include subfolders
        </CheckboxLabel>

        <ButtonGroup style={{ justifyContent: 'flex-end' }}>
          <ActionButton variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </ActionButton>
          <ActionButton onClick={handleSave} disabled={isSaving || !selectedFolder}>
            {isSaving ? 'Saving...' : 'Add Folder'}
          </ActionButton>
        </ButtonGroup>
      </ConnectionCard>
    </div>
  )
}
